import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { LoyaltyPointProductsToken, DataSourceLoadOptions } from '@sleed/bizcommerce-api';
import { pointProductsApi } from '~/lib/api/client';
import { applySort, type SortDir } from '~/lib/grid/sort';

export interface LoyaltyProductsListParams {
	skip?: number;
	take?: number;
	/** Free-text search → server filter over the product's name, code or barcode. */
	search?: string;
	/** Every row, past ones included, instead of each product's current points. */
	history?: boolean;
	/** Sort selector — a grid field the controller maps (pname/pcode/bcd/pnt/afrom/ato). */
	sortSelector?: string | null;
	sortDir?: SortDir;
}

export interface LoyaltyProductsListResult {
	items: LoyaltyPointProductsToken[];
	totalCount: number;
}

export const loyaltyProductKeys = {
	all: ['loyalty-products'] as const,
	list: (params: unknown) => ['loyalty-products', 'list', params] as const
};

/** Exported for the tests: the envelope `pointProductsApi.getAll` is called with (history goes separately). */
export function buildLoyaltyProductLoadOptions(params: LoyaltyProductsListParams): DataSourceLoadOptions {
	const { skip = 0, take = 25, search = '', sortSelector = null, sortDir = 'asc' } = params;
	const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };

	const term = search.trim();
	// The DevExtreme binder parses `filter`/`sort` from JSON strings: the generated client flattens a raw array into
	// params the binder cannot read, so the sort used to be ignored (see lib/grid/sort).
	if (term)
		(loadOptions as { filter?: unknown }).filter = JSON.stringify([
			['pname', 'contains', term],
			'or',
			['pcode', 'contains', term],
			'or',
			['bcd', 'contains', term]
		]);
	if (sortSelector) applySort(loadOptions, sortSelector, sortDir);

	return loadOptions;
}

/**
 * Server-paged loyalty product points (read-only): by default each product's (or variant's) current points, with
 * `history` every row. DevExtreme `loadOptions` envelope; the controller maps the grid's field names in sort and filter.
 */
export function useLoyaltyProductsList(params: LoyaltyProductsListParams = {}) {
	const { skip = 0, take = 25, search = '', history = false, sortSelector = null, sortDir = 'asc' } = params;
	return useQuery<LoyaltyProductsListResult>({
		queryKey: loyaltyProductKeys.list({ skip, take, search, history, sortSelector, sortDir }),
		queryFn: async () => {
			const res = (await pointProductsApi.getAll(buildLoyaltyProductLoadOptions({ skip, take, search, sortSelector, sortDir }), history))
				.data;
			return { items: res.data ?? [], totalCount: res.totalCount ?? 0 };
		},
		placeholderData: keepPreviousData
	});
}
